import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { useGetAllTenantsQuery } from "../../../customer/tenantApi";
import { useGetAllUsersListQuery } from "../../../user/users/usersApi";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import { createClickableLink } from "../../../../components/common/datatable/DataTableUtils";
import { SearchInputWrapper } from "../../../../components/common/datatable/filter/SearchInputWrapper";
import { SearchSelectWithEnum, SearchSelectWithList } from "../../../../components/common/datatable/filter/SearchSelectWrapper";
import { getEnumDisplayNameByConstant } from "../../../../helpers/utils/EnumUtils";
import { formatValueAsDateAndTime } from "../../../../helpers/utils/DateUtils";
import { SearchDatePickerWrapper } from "../../../../components/common/datatable/filter/SearchDatePickerWrapper";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import ReportType from "./ReportType";
import { DataTable } from "../../../../components/common/datatable/DataTable";
import { useLazyExportRevenueEventDataQuery } from "../../../accounting/revenueEvents/revenueEventsApi";
import { useLazyExportProductAssignmentReportDataQuery, useSearchProductAssignmentReportApiQuery } from "../report";
import { useLazyGetAllCustomerByTenantQuery } from "../../../customer/customerApi";
import {useFindUserSettingsByGroupNameQuery} from "../../../../components/common/userSetting/userSettingsApi";

const ProductAssignmentReport = () => {

    const { data: userSetting } = useFindUserSettingsByGroupNameQuery("Report Product Assignment");
    const storageData = useSelector((state: any) => state.utilDataReducer);
    const [getAllCustomerTenantApi, getAllCustomerTenantApiResponse] = useLazyGetAllCustomerByTenantQuery();
    const tenantData = useGetAllTenantsQuery("");
    const userData = useGetAllUsersListQuery("");
    const { data: enums } = useGetEnumsQuery("")
    const [type, setType] = useState('PRODUCT_ASSIGNMENT_REPORT');
    const [triggerExportReportDataQuery] = useLazyExportProductAssignmentReportDataQuery();

    useEffect(() => {
        document.title = 'Product Assignment Report';

    }, []);



    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Product Assignment Report", url: "" }]
    );

    const columns: DataTableColumn[] = [
        {
            label: "Order No.",
            accessor: "customerOrderNumber",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {

                let path = "/orders/" + props.row.original.orderId;
                return <>
                    {
                        createClickableLink(path, props.value, userSetting, false, "", ["ORDR-R"])
                    }
                </>
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Order No..."}
                    name={"customerOrderNumber"}
                    accessor={"customerOrderNumber"} />,
            }]
        },
        {
            label: "Tenant",
            accessor: "tenant",
            visibility: { canColumnShow: false, hasShowSetting: false },
            filterOptions: [{
                dependentColumns: ["Customer"],
                filterComponent: SearchSelectWithList(tenantData?.data, "name", "name", "Select Tenant...", false, "tenant"),
                overrideFilterAccessor: "tenant",
                overrideFilterLabel: "Tenant",
            }],
        },
        {
            label: "Customer",
            accessor: "customerName",
            overrideSortColumn: "customerId",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {
                return props.value;
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(getAllCustomerTenantApiResponse?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Customers...", true, "customerIds"),
                onDependencyUpdated: (value: any) => {
                    getAllCustomerTenantApi({ tenantName: value });
                },
                overrideFilterAccessor: "customerIds",
            }],
        },

        {
            label: "Product",
            accessor: "productType",
            cell: (props: { value: any; }) => {
                return <>
                    <span>
                        {getEnumDisplayNameByConstant(enums?.["ProductType"], props.value)}
                    </span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["ProductType"], "Select Product Types...", true, "productType"),
                overrideFilterAccessor: "type",
            }]
        },

        {
            label: "Status",
            accessor: "productStatus",
            cell: (props: { value: any; }) => {
                return <>
                    <span>
                        {getEnumDisplayNameByConstant(enums?.["ProductStatus"], props.value)}
                    </span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["ProductStatus"], "Select Product Statuses...", true, "productStatus"),
                overrideFilterAccessor: "productStatuses",
            }]
        },

        {
            label: "Delivered Date", accessor: "lastDeliveredDate", visibility: { hasShowSetting: false }, canSort: false,
            cell: (props: { value: any; }) => {
                return props.value ? formatValueAsDateAndTime(props.value, "MM/D/YYYY") : '';
            }, filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateFrom"} />,
                overrideFilterAccessor: "deliveredDateFrom",
                overrideFilterLabel: "Delivered Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateTo"} />,
                overrideFilterAccessor: "deliveredDateTo",
                overrideFilterLabel: "Delivered Date - To",
            }]
        },

        {
            label: "Assigned Date", accessor: "assignedDate", visibility: { hasShowSetting: false }, canSort: false,
            cell: (props: { value: any; }) => {
                return props.value ? formatValueAsDateAndTime(props.value, "MM/D/YYYY") : '';
            }, filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"assignedDateFrom"} />,
                overrideFilterAccessor: "assignedDateFrom",
                overrideFilterLabel: "Assigned Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"assignedDateTo"} />,
                overrideFilterAccessor: "assignedDateTo",
                overrideFilterLabel: "Assigned Date - To",
            }]
        },

        {
            label: "Assigner",
            accessor: "assigner",
            visibility: { hasShowSetting: false },
            filterOptions: [{
                filterComponent: SearchSelectWithList(userData?.data, "username", "id", "Select Assigners...", true, "assigner"),
                overrideFilterAccessor: "assignedTo",
                overrideFilterLabel: "Assigner",
            }],
        },

        {
            label: "Assignee Type",
            accessor: "assigneeType",
            cell: (props: { value: any; }) => {
                return <>
                    <span>
                        {getEnumDisplayNameByConstant(enums?.["AssigneeType"], props.value)}
                    </span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["AssigneeType"], "Select Assignee Type...", false, "assigneeType"),
                overrideFilterAccessor: "assigneeType",
            }]
        },


        {
            label: "Assignee",
            accessor: "assignee",
            visibility: { hasShowSetting: false },
            filterOptions: [{
                filterComponent: SearchSelectWithList(userData?.data, "username", "id", "Select Assignies...", true, "assigner"),
                overrideFilterAccessor: "assignedBy",
                overrideFilterLabel: "Assignee",
            }],
        },

        {
            label: "Reason",
            accessor: "reason",
            visibility: { hasShowSetting: false },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Reason No..."}
                    name={"reason"}
                    accessor={"reason"} />,
            }]
        }

    ];

    function hideFromGrid(columns: any[]) {
        if (storageData?.items?.customerLevel === "TENANT") {
            return columns.filter((el: { accessor: string; }) => !["tenant"].includes(el.accessor));
        } else if (storageData?.items?.customerLevel === "CUSTOMER") {
            return columns.filter((el: { accessor: string; }) => !["tenant", "customerName"].includes(el.accessor));
        }
        return columns;
    }

    const selector = (state: { getProductAssignmentReportReducer: any; }) => state.getProductAssignmentReportReducer;

    return (
        <>

            <div className="user-container px-4 py-2">
                <div className="flex justify-between">
                    <Breadcrumb crumbs={crumbs} />
                </div>
                <ReportType type={type} setType={setType} />
                <DataTable
                    columns={columns}
                    actions={{
                        fetchAction: useSearchProductAssignmentReportApiQuery,
                        exportAction: triggerExportReportDataQuery
                    }
                    }
                    defaultSortOptions={{
                        accessor: "creationDate",
                        direction: "desc"
                    }}
                    userSettingGroup={"Report-ProductAssignment"}
                    basePermission={"PAR"}
                />
            </div>
            <div />
        </>
    )
}

export default ProductAssignmentReport;